import { type DataNeededId, Dtos, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	dataNeededQueryKey,
	decisionsQueryKey,
	resolvedDataNeededQueryKey,
} from "../decisioning.keys";
import { type DataNeeded } from "../types";
import { invalidateListWhenIdle, useOptimisticListMutation } from "../useOptimisticListMutation";

/** Reopens a received or closed item: it leaves the resolved list and rejoins the open one. */
export const useReopenDataNeeded = (accountId: SuretyAccountId) => {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<DataNeeded, DataNeededId, DataNeeded>({
		queryKey: resolvedDataNeededQueryKey(accountId),
		request: async (id) =>
			Dtos.Decisioning.DataNeeded.Get.Response.schema.parse(
				await post<unknown>(`/v2/admin/surety/accounts/${accountId}/data-needed/${id}/reopen`),
			),
		apply: (items, id) => items.filter((item) => item.id !== id),
		affectedIds: (id) => [id],
		failureMessage: () => "Couldn't reopen the request.",
		reconcile: (items, reopened) => {
			queryClient.setQueryData<DataNeeded[]>(
				dataNeededQueryKey(accountId),
				(open) => open && [...open.filter((item) => item.id !== reopened.id), reopened],
			);
			void invalidateListWhenIdle(queryClient, dataNeededQueryKey(accountId));
			void invalidateListWhenIdle(queryClient, decisionsQueryKey(accountId));
			return items.filter((item) => item.id !== reopened.id);
		},
	});

	return {
		reopenDataNeeded: mutation.mutate,
	};
};
